"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { STYLE_OPTIONS, SPOKESPERSON_STYLE, isRetiredSpeakingStyle } from "@easyshot/shared";
import { StylePreview } from "./style-preview";
import { Button, cn } from "./ui";
import { PersonalStylePreview, StyleCategoryPicker, type StyleCategory } from "./style-category-picker";

/**
 * 落地页 hero 下方的工作流模板展示区。
 * 与创作向导同源：读 /api/templates（后台启用中的模板），
 * 点卡片进 /studio 并预选该模板；接口失败时用内置目录兜底。
 */

interface ShowcaseTemplate {
  id: string;
  name: string;
  description: string;
}

const FALLBACK: ShowcaseTemplate[] = [{ id: SPOKESPERSON_STYLE.id, name: SPOKESPERSON_STYLE.label, description: SPOKESPERSON_STYLE.description }, ...STYLE_OPTIONS.filter(s => !isRetiredSpeakingStyle(s.id)).map((s) => ({
  id: s.id,
  name: s.label,
  description: s.description,
}))];

export function TemplateShowcase() {
  const [templates, setTemplates] = useState<ShowcaseTemplate[]>(FALLBACK);
  const [showAllStyles, setShowAllStyles] = useState(false);
  const [category, setCategory] = useState<StyleCategory>("commercial");

  useEffect(() => {
    let alive = true;
    void fetch("/api/templates")
      .then((r) => r.json())
      .then((t: ShowcaseTemplate[]) => {
        if (alive && Array.isArray(t) && t.length) setTemplates(t);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="mx-auto mt-6 max-w-4xl">
      <StyleCategoryPicker className="justify-center" value={category} onChange={(value) => { setCategory(value); setShowAllStyles(false); }} />
      <p aria-live="polite" className="mb-4 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-[12px] leading-5 text-muted sm:text-sm">
        <span>{category === "commercial" ? "Ads · Product demos · Spokesperson videos" : "Birthdays · Weddings · Memories"}</span>
        {category === "personal" && <span className="shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-[10px] leading-4">Coming soon</span>}
      </p>
      {category === "personal" ? <PersonalStylePreview showIntro={false} /> : <>
      <div
        className={cn(
          "style-grid grid gap-3 sm:grid-cols-2 lg:grid-cols-3",
          !showAllStyles && "is-collapsed",
        )}
      >
        {templates.map((t) => (
          <Link
            key={t.id}
            href={`/studio?style=${encodeURIComponent(t.id)}`}
            className="group overflow-hidden rounded-xl border border-line bg-white/[0.04] text-left transition hover:border-[var(--border-strong)] hover:bg-white/[0.07]"
          >
            <div className="relative">
              <StylePreview styleId={t.id} />
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold">{t.name}</span>
                <span className="text-muted transition group-hover:translate-x-0.5 group-hover:text-foreground">
                  →
                </span>
              </div>
              <p className="mt-1.5 line-clamp-2 text-[12px] leading-5 text-muted">
                {t.description}
              </p>
            </div>
          </Link>
        ))}
      </div>
      {templates.length > 2 && (
        <div className="mt-3 flex justify-end">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowAllStyles((shown) => !shown)}
          >
            {showAllStyles
              ? "Show fewer"
              : `All ${templates.length} styles →`}
          </Button>
        </div>
      )}
      </>}
    </div>
  );
}
